import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import { atxNotMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import {
  ConfirmationModalService,
  ModalService,
  overOverlayModalBackdropClass,
  overOverlayModalClass,
} from '@epicuro-next/ataraxia/modal';
import {
  pulseConfigurationDefaultValues,
  PulseConfigurationKeys,
  PulseConfigurations,
  PULSE_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/pulse-rules';
import {
  ConfigurationsService,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  removeFiltersById,
  selectActiveWspFilter,
  wspFilterId,
} from '@epicuro-next/state/p-basket-state';
import {
  selectAdvancedMDOConfigAvailable,
  selectAdvancedMDOEnabled,
  selectIsSubscribedToAccountBaskets,
  selectPreventZoom,
  selectPreventZoomRule,
  selectShowDarkMode,
  selectShowWholeSalePrices,
  setAdvancedMDOMode,
  setDarkMode,
  setPreventZoom,
  setShowWholesalePrices,
  setSubscribeToAllEmails,
} from '@epicuro-next/state/user-state';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { catchError, combineLatest, EMPTY, merge, of, Subject } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { EnablingFeatureModalComponent } from '../enabling-feature-modal/enabling-feature-modal.component';

@Component({
  selector: 'epc-quick-settings',
  templateUrl: './quick-settings.component.html',
  styleUrls: ['quick-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuickSettingsComponent implements OnDestroy, OnInit {
  public advancedMDOEnabled = new UntypedFormControl();
  public showWholeSalePrices = new FormControl<boolean>(false);
  public subscribeToAllEmailsControl = new FormControl<boolean>(false);
  public darkMode = new UntypedFormControl();
  public preventZoom = new UntypedFormControl();
  private destroy$ = new Subject<void>();

  public isPreventZoomRuleEnabled$ = this.store.pipe(
    select(selectPreventZoomRule),
  );

  public isDarkModeEnabled$ = this.configurationService.getConfigValue({
    namespace: PULSE_FEATURE_NAME,
    featureName: PulseConfigurationKeys.DarkModeEnabled,
    defaultValue: false,
  });

  private darkModeControlChanges$ = this.darkMode.valueChanges.pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public showAdvancedMDO$ = combineLatest([
    this.store.pipe(select(selectAdvancedMDOConfigAvailable)),
    atxNotMobile$,
  ]).pipe(map(([available, notMobile]) => available && notMobile));

  public subscribeToAllEmailsAvailable$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        PulseConfigurationKeys.SubscribeToAllEmails,
        PulseConfigurations
      >(
        PULSE_FEATURE_NAME,
        PulseConfigurationKeys.SubscribeToAllEmails,
        pulseConfigurationDefaultValues[
          PulseConfigurationKeys.SubscribeToAllEmails
        ],
      ),
    ),
  );

  constructor(
    private store: Store,
    private configurationService: ConfigurationsService,
    private modalService: ModalService,
    private confirmService: ConfirmationModalService,
    private renderer: Renderer2,
  ) {
    this.store
      .pipe(select(selectShowWholeSalePrices), takeUntil(this.destroy$))
      .subscribe((value) =>
        this.showWholeSalePrices.setValue(value, {
          emitEvent: false,
        }),
      );

    this.store
      .pipe(
        select(selectIsSubscribedToAccountBaskets),
        map(Boolean),
        takeUntil(this.destroy$),
      )
      .subscribe((value) => this.subscribeToAllEmailsControl.setValue(value));

    this.store
      .pipe(select(selectAdvancedMDOEnabled), takeUntil(this.destroy$))
      .subscribe((value) => this.advancedMDOEnabled.setValue(value));

    this.store
      .pipe(select(selectPreventZoom), takeUntil(this.destroy$))
      .subscribe((value) => this.preventZoom.setValue(value));

    const ruleWithSystemSetting$ = combineLatest([
      this.isDarkModeEnabled$,
      this.store.select(selectShowDarkMode),
    ]).pipe(
      map(
        ([modeEnabled, darkModeIsOnByUser]) =>
          modeEnabled && darkModeIsOnByUser,
      ),
      takeUntil(this.destroy$),
    );

    merge(this.darkModeControlChanges$, ruleWithSystemSetting$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((darkModeOn) => {
        this.darkMode.setValue(darkModeOn, { emitEvent: false });
        this.colorMediaSchemaListener({ matches: darkModeOn });
        if (darkModeOn) {
          this.renderer.addClass(window.document.body, 'dark-mode');
        } else {
          this.renderer.removeClass(window.document.body, 'dark-mode');
        }
      });
  }

  public ngOnInit() {
    combineLatest([
      this.isPreventZoomRuleEnabled$,
      this.store.pipe(select(selectPreventZoom)),
      atxNotMobile$,
    ])
      .pipe(
        filter(([rule]) => rule),
        takeUntil(this.destroy$),
      )
      .subscribe(([, value, notMobile]) => {
        if (value && notMobile) {
          this.renderer.addClass(
            document.documentElement,
            'prevent-zoom-by-pixel-ratio',
          );
        } else {
          this.renderer.removeClass(
            document.documentElement,
            'prevent-zoom-by-pixel-ratio',
          );
        }
      });

    this.subscribeToWspChange();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onEnableAdvancedMDOChange(event: Event) {
    this.store.dispatch(
      setAdvancedMDOMode({
        advancedMDOMode: (event.target as HTMLInputElement).checked,
      }),
    );
    if ((event.target as HTMLInputElement).checked) {
      this.openEnablingFeatureModal({
        title: $localize`:UserMenu|Advanced MDO modal title@@advancedMDOModalTitle:You've activated the Advanced MDO!`,
        message: $localize`:UserMenu|Advanced MDO modal text@@advancedMDOModalInfoText:Advanced MDO is created for super-buyers like you to order in a fast and
        seamless way. You can always turn off and on the Advanced MDO in your
        Quick Settings.`,
        imageSrc: 'assets/advanced-MDO.svg',
      });
    }
  }

  public subscribeToAllEmailsChange(event: Event) {
    this.store.dispatch(
      setSubscribeToAllEmails({
        isSubscribedToAccountBaskets: (event.target as HTMLInputElement)
          .checked,
      }),
    );
    if ((event.target as HTMLInputElement).checked) {
      this.openEnablingFeatureModal({
        title: $localize`:UserMenu|Subscribe to all emails modal title@@enableSubscribeToEmailModalTitle:You've activated the subscription to all order copies!`,
        message: $localize`:UserMenu|Subscribe to all emails modal text@@enableSubscribeToEmailModalInfoText:Activating this option you are subscribing to receiving all order copies from orders placed by all users from this account.`,
        imageSrc: 'assets/subscribe-to-emails.svg',
      });
    }
  }

  public onPreventZoomChange(event: Event) {
    this.store.dispatch(
      setPreventZoom({
        preventZoom: (event.target as HTMLInputElement).checked,
      }),
    );
  }

  private colorMediaSchemaListener({
    matches,
  }: Pick<MediaQueryListEvent, 'matches'>) {
    const isDarkMode = matches;
    this.store.dispatch(
      setDarkMode({
        darkModeOn: isDarkMode,
      }),
    );
  }

  private openEnablingFeatureModal({
    title,
    message,
    imageSrc,
  }: {
    title: string;
    message: string;
    imageSrc: string;
  }) {
    const modalRef = this.modalService.open(EnablingFeatureModalComponent, {
      windowClass: overOverlayModalClass,
      backdropClass: overOverlayModalBackdropClass,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.imageSrc = imageSrc;
  }

  private subscribeToWspChange() {
    this.showWholeSalePrices.valueChanges
      .pipe(
        map(Boolean),
        concatLatestFrom(() =>
          this.store.pipe(select(selectActiveWspFilter), map(Boolean)),
        ),
        switchMap(([value, wspFilterEnabled]) =>
          !value && wspFilterEnabled
            ? this.confirmWspDisable().pipe(
                tap(() => {
                  this.store.dispatch(
                    removeFiltersById({
                      fieldId: wspFilterId,
                    }),
                  );
                }),
                map(() => value),
                catchError(() => {
                  this.showWholeSalePrices.setValue(!value, {
                    emitEvent: false,
                  });
                  return EMPTY;
                }),
              )
            : of(value),
        ),
        tap((value) => {
          this.store.dispatch(
            setShowWholesalePrices({
              show: value,
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  private confirmWspDisable() {
    return this.confirmService.open({
      message: $localize`:UserMenu|Advanced wsp modal text@@hideWspModalInfoText:Your P-Basket is currently filtered with the WSP-based Price range filter. Once you disable the “Show Wholesales price” switcher, the mentioned filter will be reset to the default value, and the P-Basket filtering will be updated accordingly. You can apply the RRP-based Price range filter via the filters.`,
      title: $localize`:UserMenu|Hide wsp modal title@@hideWspModalTitle:You are about to disable the "Show Wholesales price" switcher`,
      hideFooter: false,
      showCloseBtn: false,
      options: { attachBrowserHistory: false },
    });
  }
}
